import React, { useState, useEffect } from "react";
import Routes from "./routes";
import "react-toastify/dist/ReactToastify.css";

import { createTheme, ThemeProvider } from "@material-ui/core/styles";
import { enUS, esES, ptBR } from "@material-ui/core/locale";
import { i18n } from "./translate/i18n";

const buildTheme = (locale) =>
  createTheme(
    {
      typography: {
        fontFamily:
          '"Segoe UI Variable", "Segoe UI", "Helvetica Neue", Arial, sans-serif',
        h5: {
          fontWeight: 700,
          letterSpacing: "-0.02em"
        },
        h6: {
          fontWeight: 700,
          letterSpacing: "-0.01em"
        },
        subtitle1: {
          fontWeight: 600
        },
        button: {
          fontWeight: 700,
          textTransform: "none"
        }
      },
      scrollbarStyles: {
        scrollbarWidth: "thin",
        scrollbarColor: "#9ab8b1 rgba(21, 43, 55, 0.08)",
        "&::-webkit-scrollbar": {
          width: "6px",
          height: "6px",
        },
        "&::-webkit-scrollbar-track": {
          background: "rgba(21, 43, 55, 0.08)",
          borderRadius: 999,
        },
        "&::-webkit-scrollbar-thumb": {
          boxShadow: "inset 0 0 0 1px rgba(255, 255, 255, 0.35)",
          backgroundColor: "#9ab8b1",
          borderRadius: 999,
        },
      },
      palette: {
        primary: {
          main: "#176b63",
          light: "#2a8b81",
          dark: "#11524d",
          contrastText: "#ffffff",
        },
        secondary: {
          main: "#a87422",
          light: "#c8923f",
          dark: "#7d5718",
          contrastText: "#ffffff",
        },
        info: {
          main: "#3567c8",
          light: "#5e8ce2",
          dark: "#274fa0",
          contrastText: "#ffffff",
        },
        divider: "#dbe3ea",
        background: {
          default: "#f3f6f8",
          paper: "#ffffff",
        },
        text: {
          primary: "#17202b",
          secondary: "#617082",
        },
      },
      shape: {
        borderRadius: 16,
      },
      overrides: {
        MuiCssBaseline: {
          "@global": {
            body: {
              background:
                "linear-gradient(180deg, #f7f9fb 0%, #f1f5f8 44%, #edf2f6 100%)",
            },
            "#root": {
              minHeight: "100vh",
            },
            "*": {
              scrollbarWidth: "thin",
              scrollbarColor: "#9ab8b1 rgba(21, 43, 55, 0.08)",
            },
            "*::-webkit-scrollbar": {
              width: "6px",
              height: "6px",
            },
            "*::-webkit-scrollbar-track": {
              background: "rgba(21, 43, 55, 0.08)",
              borderRadius: 999,
            },
            "*::-webkit-scrollbar-thumb": {
              backgroundColor: "#9ab8b1",
              borderRadius: 999,
              border: "1px solid rgba(255, 255, 255, 0.35)",
            },
            "*::-webkit-scrollbar-thumb:hover": {
              backgroundColor: "#7ea79d",
            },
          },
        },
        MuiAppBar: {
          colorPrimary: {
            background:
              "linear-gradient(180deg, rgba(255,255,255,0.98) 0%, rgba(248,250,252,0.98) 100%)",
            color: "#17202b",
            boxShadow: "0 10px 30px rgba(15, 23, 42, 0.08)",
            borderBottom: "1px solid rgba(148, 163, 184, 0.16)",
          },
        },
        MuiDrawer: {
          paper: {
            background:
              "linear-gradient(180deg, rgba(255,255,255,0.98) 0%, rgba(247,249,251,0.98) 100%)",
            borderRight: "1px solid rgba(148, 163, 184, 0.18)",
            boxShadow: "18px 0 36px rgba(15, 23, 42, 0.06)",
          },
        },
        MuiPaper: {
          rounded: {
            borderRadius: 20,
          },
          outlined: {
            borderColor: "rgba(148, 163, 184, 0.18)",
          },
          elevation1: {
            boxShadow: "0 18px 40px rgba(15, 23, 42, 0.06)",
          },
        },
        MuiButton: {
          root: {
            borderRadius: 13,
            fontWeight: 700,
            boxShadow: "none",
            paddingLeft: 18,
            paddingRight: 18,
          },
          containedPrimary: {
            background:
              "linear-gradient(180deg, #22877d 0%, #176b63 100%)",
            color: "#f8fffb",
            boxShadow: "0 10px 24px rgba(23, 107, 99, 0.18)",
            "&:hover": {
              background:
                "linear-gradient(180deg, #299488 0%, #1b776d 100%)",
              boxShadow: "0 12px 28px rgba(23, 107, 99, 0.20)",
            },
          },
          outlinedPrimary: {
            borderColor: "rgba(23, 107, 99, 0.24)",
            background:
              "linear-gradient(180deg, rgba(255,255,255,0.98) 0%, rgba(250,252,253,0.98) 100%)",
            "&:hover": {
              background:
                "linear-gradient(180deg, rgba(246,250,252,0.98) 0%, rgba(241,246,250,0.98) 100%)",
            },
          },
        },
        MuiOutlinedInput: {
          root: {
            backgroundColor: "rgba(255, 255, 255, 0.8)",
            borderRadius: 16,
            "& .MuiOutlinedInput-notchedOutline": {
              borderColor: "rgba(148, 163, 184, 0.26)",
            },
            "&:hover .MuiOutlinedInput-notchedOutline": {
              borderColor: "rgba(96, 112, 130, 0.38)",
            },
            "&.Mui-focused .MuiOutlinedInput-notchedOutline": {
              borderColor: "#176b63",
              borderWidth: 1,
            },
          },
          input: {
            paddingTop: 15,
            paddingBottom: 15,
          },
        },
        MuiTableCell: {
          head: {
            color: "#556476",
            fontSize: 12,
            fontWeight: 700,
            letterSpacing: "0.03em",
            textTransform: "uppercase",
            borderBottom: "1px solid rgba(148, 163, 184, 0.18)",
          },
          body: {
            borderBottom: "1px solid rgba(226, 232, 240, 0.7)",
          },
        },
        MuiDialog: {
          paper: {
            borderRadius: 24,
            boxShadow: "0 30px 60px rgba(15, 23, 42, 0.16)",
          },
        },
        MuiMenu: {
          paper: {
            borderRadius: 16,
            border: "1px solid rgba(148, 163, 184, 0.16)",
            boxShadow: "0 18px 38px rgba(15, 23, 42, 0.10)",
          },
        },
        MuiTab: {
          root: {
            textTransform: "none",
            fontWeight: 700,
            minHeight: 56,
          },
        },
        MuiChip: {
          root: {
            fontWeight: 700,
            borderRadius: 999,
          },
        },
        MuiBadge: {
          colorPrimary: {
            backgroundColor: "#176b63",
          },
          colorSecondary: {
            backgroundColor: "#a87422",
          },
        },
      },
    },
    locale
  );

const getMuiLocale = language => {
  const normalized = String(language || "pt").toLowerCase().slice(0, 2);

  if (normalized === "en") return enUS;
  if (normalized === "es") return esES;
  return ptBR;
};

const normalizeLanguage = language => {
  const normalized = String(language || "pt").toLowerCase().slice(0, 2);
  return ["pt", "en", "es"].includes(normalized) ? normalized : "pt";
};

const App = () => {
  const [language, setLanguage] = useState(() =>
    normalizeLanguage(i18n.language || localStorage.getItem("i18nextLng"))
  );
  const [locale, setLocale] = useState(() =>
    getMuiLocale(language)
  );
  const theme = buildTheme(locale);

  useEffect(() => {
    const handleLanguageChanged = nextLanguage => {
      const normalizedLanguage = normalizeLanguage(nextLanguage);
      setLanguage(normalizedLanguage);
      setLocale(getMuiLocale(normalizedLanguage));
    };

    i18n.on("languageChanged", handleLanguageChanged);
    return () => i18n.off("languageChanged", handleLanguageChanged);
  }, []);

  return (
    <ThemeProvider key={language} theme={theme}>
      <Routes key={language} />
    </ThemeProvider>
  );
};

export default App;
